import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import {
  Button,
  Card,
  Col,
  Divider,
  message,
  Modal,
  Row,
  Skeleton,
  Space,
  Spin,
  Typography,
} from "antd";
import { LeftOutlined } from "@ant-design/icons";
import moment from "moment";

import "../../App.css";
import * as com from "../../Common.js";
import * as act from "../../Store/actions.js";
import "./PreQualPlanPicker.css";
import {
  parseStringToNumber,
  roundPercentage,
  USDollarCurrencyLocale,
} from "./utils";
import {
  getPrequalPDFBlobAndContent,
  PrequalLetterBrandingData,
  sendPrequalPDFToBorrowerAndSaveToDB,
} from "./utils_pdf";
import { calculateDownPayAndLoanSize, getProductType } from "./utils_loans";

const { Text, Title } = Typography;
const { confirm } = Modal;

type PreQualPlanProps = {
  borrowerID: string;
  loanID: string;
  ltv: number;
  purchasePrice: number;
  bestProduct: any;
  borrowerName: string;
  email: string;
  downpayment: number;
  loading: boolean;
  onClickSendToBorrower: () => void;
  brandingData?: PrequalLetterBrandingData;
  canGenerate: boolean;
  onValidationError: () => void;
  onPersistBranding: () => Promise<void>;
};

const PreQualPlan = forwardRef((props: PreQualPlanProps, ref) => {
  const borrowerID = props.borrowerID;
  const loanID = props.loanID;
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [sendingPdf, setSendingPdf] = useState(false);
  const [pdfURL, setPdfURL] = useState("");
  const [pdfURLForDownload, setPdfURLForDownload] = useState("");
  const [pdfDownload, setPDFDownload] = useState("");
  const [pdfPreviewModelOpen, setPdfPreviewModelOpen] = useState(false);
  const hiddenPdfDownloadButtonRef = useRef<HTMLAnchorElement>(null);
  const pdfObjectURLsRef = useRef<Set<string>>(new Set());
  const dispatch = useDispatch();

  const validateBeforeAction = () => {
    if (!props.canGenerate) {
      props.onValidationError();
      return false;
    }
    return true;
  };

  useEffect(() => {
    return () => {
      pdfObjectURLsRef.current.forEach((url) => URL.revokeObjectURL(url));
      pdfObjectURLsRef.current.clear();
    };
  }, []);

  const triggerPdfDownload = (url: string, download: string) => {
    setPdfURLForDownload(url);
    setPDFDownload(download);
    if (hiddenPdfDownloadButtonRef.current) {
      hiddenPdfDownloadButtonRef.current.href = url;
      hiddenPdfDownloadButtonRef.current.download = download;
      hiddenPdfDownloadButtonRef.current.click();
    }
  };

  const downloadPrequalPDF = async () => {
    if (!validateBeforeAction()) {
      return;
    }
    if (pdfURL) {
      triggerPdfDownload(pdfURL, pdfDownload || "prequalification.pdf");
      return;
    }
    await props.onPersistBranding();
    const { downPayment, loanSize } = calculateDownPayAndLoanSize(
      props.purchasePrice,
      props.ltv
    );
    dispatch(act.UpdateSelectField(props.downpayment, "downpayment"));
    // dispatch(act.UpdateSelectField(downPayment, "downpayment"));
    dispatch(act.UpdateSelectField(loanSize, "loansize"));

    setLoadingPdf(true);
    try {
      const { blob, download } = await getPrequalPDFBlobAndContent({
        salesPrice: props.purchasePrice,
        ltv: props.ltv,
        borrowerID,
        loanID,
        downpayment: props.downpayment,
        brandingData: props.brandingData,
      });
      const url = window.URL.createObjectURL(blob);
      pdfObjectURLsRef.current.add(url);
      triggerPdfDownload(url, download);
    } catch (error) {
      console.error("Failed to download pre-qualification letter:", error);
      message.error("Unable to download the pre-qualification letter.");
    } finally {
      setLoadingPdf(false);
    }
  };

  const previewPrequalPDF = async () => {
    if (!validateBeforeAction()) {
      return;
    }
    setPdfURL("");
    setPdfPreviewModelOpen(true);
    const { downPayment, loanSize } = calculateDownPayAndLoanSize(
      props.purchasePrice,
      props.ltv
    );
    dispatch(act.UpdateSelectField(props.downpayment, "downpayment"));
    // dispatch(act.UpdateSelectField(downPayment, "downpayment"));
    dispatch(act.UpdateSelectField(loanSize, "loansize"));
    try {
      const { blob, download } = await getPrequalPDFBlobAndContent({
        salesPrice: props.purchasePrice,
        ltv: props.ltv,
        borrowerID,
        loanID,
        downpayment: props.downpayment,
        brandingData: props.brandingData,
      });
      const blobURL = URL.createObjectURL(blob);
      pdfObjectURLsRef.current.add(blobURL);
      setPDFDownload(download);
      setPdfURL(blobURL);
    } catch (error) {
      console.error("Failed to preview pre-qualification letter:", error);
      setPdfPreviewModelOpen(false);
      message.error("Unable to preview the pre-qualification letter.");
    }
  };

  useImperativeHandle(ref, () => ({
    previewPrequalPDF,
  }));

  const backToEdit = () => {
    setPdfPreviewModelOpen(false);
    setPdfURL("");
  };

  const sendPrequalPDFToBorrower = async () => {
    if (!validateBeforeAction()) {
      return;
    }
    await props.onPersistBranding();
    console.log("Sending prequal letter to borrower");
    const { downPayment, loanSize } = calculateDownPayAndLoanSize(
      props.purchasePrice,
      props.ltv
    );
    dispatch(act.UpdateSelectField(props.downpayment, "downpayment"));
    // dispatch(act.UpdateSelectField(downPayment, "downpayment"));
    dispatch(act.UpdateSelectField(loanSize, "loansize"));

    setSendingPdf(true);
    try {
      await sendPrequalPDFToBorrowerAndSaveToDB({
        salesPrice: props.purchasePrice,
        ltv: props.ltv,
        borrowerID,
        loanID,
        email: props.email,
        borrowerName: props.borrowerName,
        downpayment: props.downpayment,
        brandingData: props.brandingData,
      });
    } catch (error) {
      console.error("Failed to send pre-qualification letter:", error);
      message.error("Unable to send the pre-qualification letter.");
      return;
    } finally {
      setSendingPdf(false);
    }
    setPdfPreviewModelOpen(false);

    props.onClickSendToBorrower();
    confirm({
      title: (
        <Row justify="center">
          <img
            src="/images/emailSent.svg"
            alt="Email Sent"
            style={{ height: "4em", padding: "10px" }}
          />
        </Row>
      ),
      width: "80vw",
      content: (
        <Row justify="center" style={{ width: "100%" }}>
          <Row className="letter_sent_container">
            <Col span={6} />
            <Col span={12}>
              <Text className="letter_sent_title">Pre-qual letter sent!</Text>
              <br />
              <Text className="letter_sent_text">
                Your pre-qualification has been sent to your borrower's email
                and their Zeitro account.
              </Text>
            </Col>
            <Col span={6} />
          </Row>
          {props.bestProduct && (
            <Card
              style={{ width: "100%" }}
              className="product_details_container"
            >
              <Row className="product_details_text_container">
                <Text className="product_details_text">
                  Here is the best product we found for your borrower, with
                  either the lowest rates or the lowest closing costs when rates
                  are equal.
                </Text>
              </Row>

              <Card className="loan_rates_container">
                <Row gutter={16} className="loan_details">
                  <Col span={6} className="header">
                    Term, Type
                  </Col>
                  <Col span={6} className="header">
                    Rate/APR
                  </Col>
                  <Col span={6} className="header">
                    Est closing cost
                  </Col>
                  <Col span={6} className="header">
                    Est mo. payment
                  </Col>
                </Row>
                <Row gutter={16} className="loan_details">
                  <Col span={6} className="content">
                    {`${props.bestProduct.Term} years, ${getProductType(
                      props.bestProduct
                    )}`}
                  </Col>
                  <Col span={6} className="content">
                    {`${props.bestProduct.base_rate.toFixed(3)}%`}/
                    {`${props.bestProduct.APR}%`}
                  </Col>
                  <Col span={6} className="content">
                    {USDollarCurrencyLocale.format(
                      Number(props.bestProduct.closing_cost)
                    )}
                  </Col>
                  <Col span={6} className="content">
                    {USDollarCurrencyLocale.format(
                      com.safeParseInt(props.bestProduct.monthly_payment)
                    )}
                  </Col>
                </Row>
              </Card>
            </Card>
          )}
        </Row>
      ),
      okText: "Done",
      okButtonProps: {
        style: {
          display: "block",
          margin: "0 auto",
        },
      },
      cancelButtonProps: { style: { display: "none" } },
      icon: null,
    });
  };

  return (
    <div className="preQualPlanContainer">
      <a
        className="hiddenPdfDownloadButton"
        hidden
        ref={hiddenPdfDownloadButtonRef}
        href={pdfURLForDownload}
        download={pdfDownload}
      >
        &nbsp;
      </a>
      {props.loading ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : (
        <div className="preQualPlanContent">
          <div className="prequal_summary_scroll">
            <div className="loan_details_container">
              <Row className="loan_details_title">Loan details</Row>
              <Row className="loan_details_row">
                <Col span={12} className="loan_details_label">
                  Purchase price:
                </Col>
                <Col span={12} className="loan_details_value">
                  {USDollarCurrencyLocale.format(props.purchasePrice)}
                </Col>
              </Row>
              <Row className="loan_details_row">
                <Col span={12} className="loan_details_label">
                  Loan amount:
                </Col>
                <Col span={12} className="loan_details_value">
                  {USDollarCurrencyLocale.format(
                    Math.round(props.purchasePrice - props.downpayment)
                    // Math.round((props.ltv * props.purchasePrice) / 100)
                  )}
                </Col>
              </Row>
              <Row className="loan_details_row">
                <Col span={12} className="loan_details_label">
                  Down payment:
                </Col>
                <Col span={12} className="loan_details_value">
                  {USDollarCurrencyLocale.format(
                    // Math.round((props.purchasePrice * (100 - props.ltv)) / 100)
                    Math.round(props.downpayment)
                  )}
                  <span className="loan_details_percent">
                    ({roundPercentage(100 - props.ltv)}%)
                  </span>
                </Col>
              </Row>
              <Row className="loan_details_row">
                <Col span={12} className="loan_details_label">
                  LTV:
                </Col>
                <Col span={12} className="loan_details_value">
                  {roundPercentage(props.ltv)}%
                </Col>
              </Row>
              <Row className="loan_details_row">
                <Col span={12} className="loan_details_label">
                  Expiration date:
                </Col>
                <Col span={12} className="loan_details_value">
                  {moment().add(30, "days").format("MMMM DD, YYYY")}
                </Col>
              </Row>
            </div>
            <div className="loan_details_disclaimer">
              Borrower's DTI is calculated at 36%.
            </div>
          </div>

          <div className="prequal_action_stack">
            <div
              className="prequal_action_guard"
              onClick={
                !props.loading && !props.canGenerate
                  ? props.onValidationError
                  : undefined
              }
            >
              <Button
                disabled={!!props.loading || !props.canGenerate}
                type="primary"
                className="prequal_send_button"
                onClick={sendPrequalPDFToBorrower}
              >
                <Space>
                  {sendingPdf && <Spin />}
                  <Text style={{ color: "white" }}>Send to borrower</Text>
                </Space>
              </Button>
            </div>
            <div
              className="prequal_action_guard"
              onClick={
                !props.loading && !props.canGenerate
                  ? props.onValidationError
                  : undefined
              }
            >
              <Button
                disabled={!!props.loading || !props.canGenerate}
                type="default"
                className="prequal_preview_button"
                onClick={previewPrequalPDF}
              >
                <Text>Preview letter</Text>
              </Button>
            </div>
            {!props.canGenerate && (
              <button
                type="button"
                className="prequal_action_validation"
                onClick={props.onValidationError}
              >
                Complete the highlighted field to continue.
              </button>
            )}
          </div>
        </div>
      )}

      <Modal
        className="prequal_pdf_preview_modal"
        title={
          <Row className="prequal_pdf_preview_title">
            <LeftOutlined
              className="prequal_pdf_preview_back"
              rev={null}
              onClick={backToEdit}
            />
            <span className="prequal_pdf_preview_title_text">
              Preview pre-qualification letter
            </span>
          </Row>
        }
        open={pdfPreviewModelOpen}
        footer={null}
        onCancel={() => {
          setPdfPreviewModelOpen(false);
        }}
        width="85%"
        zIndex={1100}
        style={{ top: 0, paddingBottom: 0 }}
      >
        {pdfURL ? (
          <div className="prequal_pdf_preview_content">
            <iframe
              src={pdfURL}
              className="prequal_pdf_preview_frame"
              title="Pre-qualification letter preview"
            />

            <Row className="prequal_pdf_preview_actions" justify="space-between">
              <Col span={16}></Col>
              <Col
                span={8}
                style={{ display: "flex", justifyContent: "space-between" }}
              >
                <Col>
                  <Button
                    type="text"
                    style={{ marginTop: "20px", height: "3em" }}
                    onClick={downloadPrequalPDF}
                  >
                    <Space>
                      {loadingPdf && <Spin />}
                      <Text
                        style={{
                          color: "#325CEB",
                          fontWeight: 600,
                          fontSize: "16px",
                        }}
                      >
                        Download
                      </Text>
                    </Space>
                  </Button>
                </Col>
                <Col>
                  <Button
                    type="primary"
                    style={{ marginTop: "20px", height: "3em" }}
                    onClick={sendPrequalPDFToBorrower}
                  >
                    <Space>
                      {sendingPdf && <Spin />}
                      <Text
                        style={{
                          color: "white",
                          fontWeight: 600,
                          fontSize: "16px",
                        }}
                      >
                        Send to borrower
                      </Text>
                    </Space>
                  </Button>
                </Col>
              </Col>
            </Row>
          </div>
        ) : (
          <div className="prequal_pdf_preview_loading">
            <Spin size="large" />
            <Text className="prequal_pdf_preview_loading_text">
              Your prequalification letter is being generated. This may take
              1-2 minutes.
            </Text>
          </div>
        )}
      </Modal>
    </div>
  );
});

export default PreQualPlan;
